import { assign, map, mapValues } from "lodash";
import { createSelector } from "reselect";
import { Map } from "immutable";
import { denormalize } from "normalizr";

import {
    canPermissionSelectorFactory,
    levelPermissionSelectorFactory,
} from "@auth/selectors";
import { AuthNormalisedEntities } from "@auth/models";
import { getEntities as getAuthEntities } from "@auth/selectors";
import { FilesNormalisedEntities } from "@files/models";
import { getEntities as getFilesEntities } from "@files/selectors";
import { MusicNormalisedEntities } from "@music/models";
import { getEntities as getMusicEntities } from "@music/selectors";
import { makeGetNextPage, makeHasMorePages } from "@orm/selectors";
import { REPORT_TYPE_CUSTOM_IDS } from "../constants";
import { getInitialState } from "../reducers";
import {
    ReportsState as State,
    Report,
    NReport,
    ReportsNormalisedEntities,
    ReportType,
    ReportKeys,
} from "../models";

export const getEntities = (state: State) =>
    state.reports.entities ? state.reports.entities : getInitialState().entities;

const makeGetList = (key: ReportKeys) => (state: State) =>
    state.reports[key].list ? state.reports[key].list : [];
export const getListCustomReports = makeGetList("customReports");
export const getListStatements = makeGetList("statements");

const makeGetLastLoaded = (key: ReportKeys) => (state: State) =>
    state.reports[key].lastLoaded;
export const getLastLoadedCustomReports = makeGetLastLoaded("customReports");
export const getLastLoadedStatements = makeGetLastLoaded("statements");

const makeGetPagination = (key: ReportKeys) => (state: State) =>
    state.reports[key].pagination;
export const getPaginationCustomReports = makeGetPagination("customReports");
export const getPaginationStatements = makeGetPagination("statements");

export const getRequestIdCustomReports = (state: State) => state.reports.customReports.requestId ?? null;

export const getReports = createSelector(
    getEntities,
    (entities: ReportsNormalisedEntities) => entities.reports,
);

export const getReportTypes = createSelector(
    getEntities,
    (entities: ReportsNormalisedEntities) => entities.reportTypes,
);

export const getCustomReportTypes = createSelector(
    getReportTypes,
    (reportTypes: Map<number, ReportType>) =>
        reportTypes.filter(
            ({ id }: ReportType) => REPORT_TYPE_CUSTOM_IDS.includes(id),
        ).sortBy(({ id }: ReportType) => id).toArray(),
);

export const hasReportInProgress = createSelector(
    getReports,
    (reports: Map<number, Report>) => reports.some((report: Report) => report.availableAt > new Date()),
);

export const getNumReports = createSelector(
    getReports,
    (reports: Map<number, Report>) => reports.size,
);

type GetListSelector = (state: any, props?: any) => number[];

export const makeGetReportsList = (getList: GetListSelector) => createSelector(
    // Get array of entities
    getList,
    getEntities,
    getFilesEntities,
    getMusicEntities,
    getAuthEntities,
    (
        list: number[],
        entities: ReportsNormalisedEntities,
        files: FilesNormalisedEntities,
        music: MusicNormalisedEntities,
        auth: AuthNormalisedEntities,
    ) => {
        // Merge the entities
        const merged = mapValues(
            assign({}, entities, files, music, auth),
            (m: Map<any, any>) => m.toJS(),
        );
        const hydrate = makeHydrateReport(merged);
        // Only return existing hydrated reports.
        return map(list, hydrate).filter((report: Report) => report != null);
    },
);

export const getStatementsList = makeGetReportsList(getListStatements);
export const getCustomReportsList = makeGetReportsList(getListCustomReports);

// Pagination selectors
export const hasMoreStatementsPages = makeHasMorePages(getPaginationStatements);
export const getNextStatementsPage = makeGetNextPage(getPaginationStatements);
export const hasMoreCustomReportsPages = makeHasMorePages(getPaginationCustomReports);
export const getNextCustomReportsPage = makeGetNextPage(getPaginationCustomReports);

// Hydrate
export const makeHydrateReport =
    (merged: ReportsNormalisedEntities) =>
        (e: number | Report): Report => {
            // Use the normalizr schema to denormalise.
            if (e != null && typeof e !== "number") {
                e = e.id;
            }
            const denormalisedData = denormalize(e, NReport, merged);

            if (denormalisedData == null) {
                return null;
            }

            // Create the report object
            e = new Report(denormalisedData);

            // Set the computed data
            e = e.set("hydrated", true) as Report;

            return e;
        };

// Permissions selectors
const reportPermissionPrefix = "wata.reporting.report";
export const canViewReport = canPermissionSelectorFactory(reportPermissionPrefix, "view");
export const getViewReportLevel = levelPermissionSelectorFactory(reportPermissionPrefix, "view");
export const canGenerateReport = canPermissionSelectorFactory(reportPermissionPrefix, "generate");
export const getGenerateReportLevel = levelPermissionSelectorFactory(reportPermissionPrefix, "generate");
